import React from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogTitle, Stack, Typography } from '@mui/material';

import { useDeleteAffiliation } from '~/hooks/apis/agent/useDeleteAffiliation';

export interface DeleteAffiliationModalData {
  id: string | null;
  name: string;
  open: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: DeleteAffiliationModalData;
}

export default function DeleteAffiliationModal({ isOpen, onClose, data }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const deleteAffiliationMutation = useDeleteAffiliation({
    onSuccess: () => {
      enqueueSnackbar('Affiliation deleted successfully');
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'There was an error deleting this affiliation', { variant: 'error' });
    },
  });

  function handleDeleteAffiliation() {
    if (data.id) {
      deleteAffiliationMutation.mutate(data.id);
    }
  }

  const isDeleting = deleteAffiliationMutation.isPending;

  return (
    <Dialog open={isOpen} fullWidth onClose={onClose}>
      <DialogTitle>Delete affiliation</DialogTitle>
      <Stack direction="row" alignItems="center" padding={3} pt={0}>
        <Stack spacing={2}>
          <Typography variant="body1">
            Are you sure you want to delete the <Typography fontWeight="bold">"{data?.name}"</Typography> affiliation?
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="flex-end" gap={2} padding={3} pt={0}>
        <Button onClick={onClose} variant="outlined" disabled={isDeleting}>
          No
        </Button>
        <Button variant="contained" onClick={handleDeleteAffiliation} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Yes'}
        </Button>
      </Stack>
    </Dialog>
  );
}
